import React, {useMemo, useCallback, useEffect, useState} from "react";
import {useTable, useSortBy} from "react-table";
import DefaultItem from "./DefaultItem.jsx";
import CourseItem from "./CourseItem.jsx";
import TwoColumnItem from "./TwoColumnItem.jsx";
import SkeletonItem from "./SkeletonItem.jsx";
import CommitteeMemberItem from "./CommitteeMemberItem.jsx";
import SessionItem from "./SessionItem.jsx";

// add any special result types here
const ResultTypes = {
  professional_series_courses: CourseItem,
  issues: TwoColumnItem,
  committee_membership: CommitteeMemberItem,
  sessions: SessionItem
};

const getResultListClassName = (type) => {
  let defaultClassName = `${type}-results-list`;
  switch (type) {
    case "issues":
    case "newsletters":
    case "education": // todo: this needs to be changed to w/e education actually is
      return `${defaultClassName} md:grid md:grid-cols-2 gap-y-8 md:gap-x-24`;
    default:
      return `${defaultClassName}`;
  }
};


export default function ResultList({
                                     title,
                                     type,
                                     error,
                                     items,
                                     loading = false,
                                     initialLoad = true
                                   }) {


  function openCity(evt, cityName) {
    // Declare all variables
    var i, tabcontent, tablinks;

    // Get all elements with class="tabcontent" and hide them
    tabcontent = document.getElementsByClassName("tabcontent");
    for (i = 0; i < tabcontent.length; i++) {
      tabcontent[i].style.display = "none";
    }

    // Get all elements with class="tablinks" and remove the class "active"
    tablinks = document.getElementsByClassName("tablinks");
    for (i = 0; i < tablinks.length; i++) {
      tablinks[i].className = tablinks[i].className.replace(" active", "");
    }

    // Show the current tab, and add an "active" class to the button that
    // opened the tab
    document.getElementById(cityName).style.display = "block";
    evt.currentTarget.className += " active";
  }


  const [sortBy, setSortBy] = useState([]);

  useEffect(() => {
    // Reset sorting state whenever items prop changes

    if (setSortBy) {
      setSortBy(sortBy);
    }
  }, [sortBy, setSortBy]);


  const columns = useMemo(
    () => [
      {

        Header: "Name",
        accessor: "title",
        sortType: "alphanumeric"
      },
      {
        Header: "Association",
        accessor: "organization_representing",
        sortType: "alphanumeric"
      },
      {
        Header: "State",
        accessor: "state.full",
        sortType: "alphanumeric"
      },
      {
        Header: "Phone",
        accessor: "phone",
        sortType: "alphanumeric"
      }
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow
  } = useTable(
    {
      columns,
      data: items,
      initialState: {sortBy: sortBy}
    },
    useSortBy // enable sorting
  );

  const toggleSortBy = useCallback(
    (column) => {
      const columnId = column.accessor;
      const isDesc = sortBy[0]?.id === columnId && !sortBy[0]?.desc;
      setSortBy([{id: columnId, desc: isDesc}]);
    },
    [sortBy, setSortBy]
  );

  title = title.replace("&nbsp;", "").replace("&amp;", "&");
  const ResultItem = useMemo(
    () => (ResultTypes[type] ? ResultTypes[type] : DefaultItem),
    [type]
  );


  const resultListClassName = useMemo(
    () => getResultListClassName(type),
    [type]
  );

  if (error) {
    return (
      <div>
        <p>
          An error has occurred while returning results. Please try changing
          filters.
        </p>
      </div>
    );
  }

  if (!items.length && !loading) {
    return (
      <div>
        <p className="text-xl text-primary">No results found.</p>
      </div>
    );
  }

  const pathName = window.location.pathname;
  if (pathName === '/atae/member-directory' || pathName === '/atae/directory/member-directory') {
    return (
      <div>
        <div
          className="flex space-x-0 items-center justify-between mb-12 md:max-w-lg">

        </div>


        <div
          className="flex space-x-0 items-center justify-between mb-12 md:max-w-lg">
          <h2 className="block text-xl text-primary">
            {initialLoad ? "Please Wait Loading ..." : (title || "Results")}{" "}
            <span className="sr-only">for {type}</span>
          </h2>
          {type === "committee_membership" && (
            <a
              href={!items[0].has_atae_access ? "/atae/member-directory/export" : "/atae/member-directory/export-full"}
              className="text-lg text-gray-700"
            >
              {/* @todo: maybe actually just use the svg here? */}
              <img
                src="/themes/custom/nada/images/icons/download-icon.svg"
                alt=""
                className="align-middle mr-4 h-4 w-4"
              />
              Download Directory
            </a>
          )
          }
        </div>


        <div className={`${resultListClassName} tabcontent`} id="list"
             style={{display: "block"}}>

          <table
            className="atae_member_directory_table ataeDictionary  sm:table-fixed  table-fixed" {...getTableProps()}>
            <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className="cursor-pointer unselectable"
                  >
                    {column.render("Header")}
                    {column.isSorted ? (column.isSortedDesc ? " ▼" : " ▲") : " - "}
                  </th>
                ))}
              </tr>
            ))}
            </thead>

            <tbody {...getTableBodyProps()}>
            {rows.map(row => {
              prepareRow(row);
              const item = row.original;
              if (item.skeleton) {
                return <SkeletonItem key={`skeleton-${row.index}`}
                                     idx={row.index}/>;
              }
              const key = item.nid || item.id;
              return <ResultItem key={key} {...item}></ResultItem>;
            })}
            </tbody>
          </table>
        </div>

      </div>
    );
  }
  else {
    return (
      <div>
        <div
          className="flex space-x-0 items-center justify-between mb-12 md:max-w-lg">
          <h2 className="block text-xl text-primary">
            {initialLoad ? "Loading" : (title || "Results")}{" "}
            <span className="sr-only">for {type}</span>
          </h2>
          {type === "committee_membership" && (
            <a
              href={!items[0].has_atae_access ? "/atae/member-directory/export" : "/atae/member-directory/export-full"}
              className="text-lg text-gray-700"
            >
              {/* @todo: maybe actually just use the svg here? */}
              <img
                src="/themes/custom/nada/images/icons/download-icon.svg"
                alt=""
                className="align-middle mr-4 h-4 w-4"
              />
              Download Directory
            </a>
          )}
        </div>

        <div className={resultListClassName}>
          {items.map((result, idx) => {
            if (result.skeleton) {
              return <SkeletonItem key={`skeleton-${idx}`} idx={idx}/>;
            }
            const key = result.nid || result.id;
            return <ResultItem key={key} {...result}></ResultItem>;
          })}
        </div>
      </div>
    );

  }

}


